import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Search, Tag } from 'lucide-react';
import api from '../../api';
import { getCategoryPath } from '../../utils/categoryTree';

const parseUTC = (iso) => {
  if (!iso) return null;
  if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso.replace(' ', 'T') + 'Z');
  return new Date(iso);
};

export default function Stock() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const r = await api.get(`/stock/${user?.id}`, { params: search ? { search } : {} });
      setStockItems(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id]);

  const loadCategories = useCallback(async () => {
    if (!user?.id) return;
    try { const r = await api.get(`/catalog-categories/${user.id}`); setCategories(r.data); } catch (e) { console.error(e); }
  }, [user?.id]);

  useEffect(() => { load(); loadCategories(); }, [load, loadCategories]);

  useEffect(() => {
    const t = setTimeout(() => load(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm, load]);

  const openAddModal = () => {
    setEditingItem(null);
    setDescription('');
    setQuantity('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setDescription(item.description);
    setQuantity(item.quantity.toString());
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!description.trim() || !quantity) {
      alert('Completa todos los campos');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/stock/${editingItem.id}`, {
          description: description.trim(),
          quantity: parseInt(quantity)
        });
        alert('Stock actualizado');
      } else {
        await api.post('/stock', {
          business_user_id: user.id,
          description: description.trim(),
          quantity: parseInt(quantity)
        });
        alert('Stock agregado');
      }
      setShowModal(false);
      load(searchTerm);
    } catch (e) {
      alert('Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este item del stock?')) return;
    try {
      await api.delete(`/stock/${id}`);
      load(searchTerm);
    } catch (e) { alert('Error al eliminar'); }
  };

  const lowStockItems = stockItems.filter(s => s.is_low_stock);

  if (loading) return <div className="page-full"><div className="spinner" style={{ marginTop: '40vh' }} /></div>;

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" data-testid="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title">Stock</h1>
        <button className="add-btn" data-testid="add-stock-btn" onClick={openAddModal}>+</button>
      </div>

      <div className="content-scroll">
        {/* Search by product or category/subcategory */}
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <Search size={16} color="var(--text2)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            className="input"
            data-testid="stock-search-input"
            placeholder="Buscar por producto, categoría o subcategoría..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>

        {/* Low Stock Alert */}
        {lowStockItems.length > 0 && (
          <div className="low-stock-alert" data-testid="low-stock-alert">
            <div className="alert-icon">⚠️</div>
            <div className="alert-content">
              <strong>¡Stock Bajo!</strong>
              <p>{lowStockItems.length} producto(s) con poco stock</p>
              <div className="alert-items">
                {lowStockItems.map(item => (
                  <span key={item.id} className="alert-item-tag">
                    {item.description}: {item.quantity}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {stockItems.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📦</span>
            <p className="empty-text">{searchTerm ? 'Sin resultados para tu búsqueda' : 'No hay items en stock'}</p>
            <p className="empty-subtext">{searchTerm ? 'Probá con otro término' : 'Presiona + para agregar stock'}</p>
          </div>
        ) : (
          <div>
            <h2 className="section-title">Inventario ({stockItems.length})</h2>
            {stockItems.map(item => (
              <div 
                key={item.id} 
                className={`card-row ${item.is_low_stock ? 'low-stock-card' : ''}`}
                data-testid={`stock-item-${item.id}`}
              >
                <div className="card-row-info">
                  <div className="card-row-title">{item.description}</div>
                  {item.category_id && (
                    <div className="card-row-detail" style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent)' }}>
                      <Tag size={11} /> {getCategoryPath(categories, item.category_id) || 'Categoría eliminada'}
                    </div>
                  )}
                  <div className="card-row-detail">
                    Agregado: {parseUTC(item.created_at)?.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div className={`stock-quantity ${item.is_low_stock ? 'low' : ''}`}>
                      {item.quantity}
                    </div>
                    {item.is_low_stock && <span className="low-stock-badge">⚠️ Bajo</span>}
                  </div>
                  <div className="card-row-actions">
                    <button className="icon-btn-sm" onClick={() => openEditModal(item)}>✏️</button>
                    <button className="icon-btn-sm danger" onClick={() => handleDelete(item.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{editingItem ? 'Editar Stock' : 'Agregar Stock'}</h3>
            
            <div className="form-group">
              <label className="label">Descripción</label>
              <input
                className="input"
                placeholder="Ej: Shampoo, Crema, etc."
                value={description}
                onChange={e => setDescription(e.target.value)}
                data-testid="stock-description-input"
              />
            </div>

            <div className="form-group">
              <label className="label">Cantidad</label>
              <input
                className="input"
                type="number"
                placeholder="0"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                min="0"
                data-testid="stock-quantity-input"
              />
            </div>

            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-stock-btn">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
